// MARK://dhk8/pos/features/billing/customers-page
// Clientes fiscales (receptores CFDI). Tabs: "Directorio" / "Nuevo cliente".
// Ported from sc-restaurant's CustomersPage, trimmed to the POS-manager surface.
//
// Wired to clients/customers (stub-mode in dev — TODO backend dhk8-erp-customers).

import { useEffect, useMemo, useState } from 'react'
import { Users } from 'lucide-react'

import { DataTable, DataTableToolbar, DataTableSearch, type ColumnDef } from '../../components/DataTable.js'
import {
  createCustomer,
  listCustomers,
  updateCustomer,
  deleteCustomer,
  type CustomerDto,
  type CustomerInput,
} from '../../lib/clients/customers.js'
import { billingStyles } from './billing-styles.js'
import { useConfirm } from '../../components/ConfirmDialog.js'

type Tab = 'directory' | 'new'

const EMPTY_FORM: CustomerInput = {
  rfc: '',
  legalName: '',
  tradeName: '',
  email: '',
  phone: '',
  zipCode: '',
  defaultCfdiUsage: 'G03',
  defaultPaymentForm: '03',
  fiscalRegimeCode: '601',
}

export default function CustomersPage() {
  const [tab, setTab] = useState<Tab>('directory')
  const confirm = useConfirm()
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState<CustomerDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [form, setForm] = useState<CustomerInput>(EMPTY_FORM)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const reload = useMemo(
    () => (signal?: AbortSignal) => {
      setLoading(true)
      setError(null)
      listCustomers(search || undefined, signal)
        .then((c) => setRows(c))
        .catch((e) => setError(e instanceof Error ? e.message : 'No se pudieron cargar los clientes'))
        .finally(() => setLoading(false))
    },
    [search],
  )

  useEffect(() => {
    const ctrl = new AbortController()
    reload(ctrl.signal)
    return () => ctrl.abort()
  }, [reload])

  const columns: ColumnDef<CustomerDto>[] = [
    { key: 'legalName', label: 'Razón social', sortable: true, sortValue: (r) => r.legalName.toLowerCase() },
    { key: 'rfc', label: 'RFC', sortable: true, sortValue: (r) => r.rfc },
    { key: 'fiscalRegimeCode', label: 'Régimen', hideOnNarrow: true },
    { key: 'zipCode', label: 'C.P.', hideOnNarrow: true },
    { key: 'email', label: 'Correo', hideOnNarrow: true },
    { key: 'actions', label: '', align: 'end' },
  ]

  const renderCell = (r: CustomerDto, c: ColumnDef<CustomerDto>) => {
    switch (c.key) {
      case 'legalName':
        return <span>{r.legalName}{r.tradeName ? <><br /><small style={{ color: 'var(--pos-fg-tertiary, var(--text-tertiary, #7E8C82))' }}>{r.tradeName}</small></> : null}</span>
      case 'rfc':
        return <code>{r.rfc}</code>
      case 'fiscalRegimeCode':
        return r.fiscalRegimeCode ?? '—'
      case 'zipCode':
        return r.zipCode ?? '—'
      case 'email':
        return r.email ?? '—'
      case 'actions':
        return (
          <button
            className="dhk8-bil__btn"
            onClick={(e) => {
              e.stopPropagation()
              setEditingId(r.id)
              setForm({
                rfc: r.rfc,
                legalName: r.legalName,
                tradeName: r.tradeName ?? '',
                email: r.email ?? '',
                phone: r.phone ?? '',
                zipCode: r.zipCode ?? '',
                defaultCfdiUsage: r.defaultCfdiUsage ?? 'G03',
                defaultPaymentForm: r.defaultPaymentForm ?? '03',
                fiscalRegimeCode: r.fiscalRegimeCode ?? '601',
              })
              setTab('new')
            }}
          >
            Editar
          </button>
        )
      default:
        return null
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.rfc.trim() || !form.legalName.trim() || !form.zipCode.trim()) {
      setError('RFC, razón social y C.P. son obligatorios.')
      return
    }
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      if (editingId) {
        await updateCustomer(editingId, form)
        setNotice('Cliente actualizado.')
      } else {
        await createCustomer(form)
        setNotice('Cliente creado.')
      }
      setForm(EMPTY_FORM)
      setEditingId(null)
      setTab('directory')
      reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el cliente')
    } finally {
      setBusy(false)
    }
  }

  async function removeCustomer(id: string) {
    if (!(await confirm({
      title: 'Eliminar cliente',
      message: 'No se podrá deshacer.',
      confirmLabel: 'Eliminar',
      tone: 'danger',
    }))) return
    setBusy(true)
    try {
      await deleteCustomer(id)
      setNotice('Cliente eliminado.')
      reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo eliminar')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="dhk8-bil">
      <header className="dhk8-bil__head">
        <h1 className="dhk8-bil__title">
          <Users size={18} className="dhk8-bil__title-ico" aria-hidden />
          Clientes fiscales
        </h1>
        <p className="dhk8-bil__sub">Receptores CFDI 4.0. Datos: RFC, régimen fiscal, C.P., uso CFDI por defecto.</p>
      </header>

      <div className="dhk8-bil__tabs" role="tablist">
        <button role="tab" aria-current={tab === 'directory'} className="dhk8-bil__tab" onClick={() => { setTab('directory'); setEditingId(null); setForm(EMPTY_FORM) }}>Directorio</button>
        <button role="tab" aria-current={tab === 'new'} className="dhk8-bil__tab" onClick={() => setTab('new')}>{editingId ? 'Editar cliente' : 'Nuevo cliente'}</button>
      </div>

      {error && <div className="dhk8-bil__error">{error}</div>}
      {notice && <div className="dhk8-bil__notice">{notice}</div>}

      {tab === 'directory' && (
        <DataTable
          columns={columns}
          rows={rows}
          getRowKey={(r) => r.id}
          renderCell={renderCell}
          onRowClick={(r) => removeCustomerPromptOrEdit(r)}
          loading={loading}
          pageSize={20}
          primaryKey="legalName"
          ariaLabel="Clientes"
          emptyState={<span>Sin clientes. Crea uno en la pestaña "Nuevo cliente".</span>}
          toolbar={
            <DataTableToolbar>
              <DataTableSearch placeholder="Buscar por RFC o razón social…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Buscar cliente" />
            </DataTableToolbar>
          }
        />
      )}

      {tab === 'new' && (
        <form className="dhk8-bil__card" onSubmit={submit}>
          <h2>{editingId ? 'Editar cliente' : 'Nuevo cliente'}</h2>
          <div className="dhk8-bil__form">
            <label>RFC *<input value={form.rfc} onChange={(e) => setForm((f) => ({ ...f, rfc: e.target.value.toUpperCase() }))} maxLength={13} required /></label>
            <label>Razón social *<input value={form.legalName} onChange={(e) => setForm((f) => ({ ...f, legalName: e.target.value }))} required /></label>
            <label>Nombre comercial<input value={form.tradeName ?? ''} onChange={(e) => setForm((f) => ({ ...f, tradeName: e.target.value }))} /></label>
            <label>Régimen fiscal *<input value={form.fiscalRegimeCode} onChange={(e) => setForm((f) => ({ ...f, fiscalRegimeCode: e.target.value }))} placeholder="601, 612, 616…" required /></label>
            <label>C.P. *<input value={form.zipCode} onChange={(e) => setForm((f) => ({ ...f, zipCode: e.target.value }))} maxLength={5} required /></label>
            <label>Uso CFDI por defecto<input value={form.defaultCfdiUsage ?? ''} onChange={(e) => setForm((f) => ({ ...f, defaultCfdiUsage: e.target.value }))} placeholder="G03, S01…" /></label>
            <label>Forma de pago por defecto<input value={form.defaultPaymentForm ?? ''} onChange={(e) => setForm((f) => ({ ...f, defaultPaymentForm: e.target.value }))} placeholder="01, 03, 04…" /></label>
            <label>Correo<input type="email" value={form.email ?? ''} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></label>
            <label>Teléfono<input value={form.phone ?? ''} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></label>
          </div>
          <div className="dhk8-bil__actions">
            <button type="submit" className="dhk8-bil__btn dhk8-bil__btn--primary" disabled={busy}>{busy ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Crear cliente'}</button>
            {editingId && <button type="button" className="dhk8-bil__btn" disabled={busy} onClick={() => removeCustomer(editingId)}>Eliminar</button>}
            <button type="button" className="dhk8-bil__btn" onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setTab('directory') }}>Cancelar</button>
          </div>
        </form>
      )}

      <style>{billingStyles}</style>
    </div>
  )

  // Row click on directory: jump to edit form prefilled.
  function removeCustomerPromptOrEdit(r: CustomerDto) {
    setEditingId(r.id)
    setForm({
      rfc: r.rfc,
      legalName: r.legalName,
      tradeName: r.tradeName ?? '',
      email: r.email ?? '',
      phone: r.phone ?? '',
      zipCode: r.zipCode ?? '',
      defaultCfdiUsage: r.defaultCfdiUsage ?? 'G03',
      defaultPaymentForm: r.defaultPaymentForm ?? '03',
      fiscalRegimeCode: r.fiscalRegimeCode ?? '601',
    })
    setTab('new')
  }
}
